import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const counties = [
  {
    name: "Cook County",
    description: "Serving Chicago and surrounding suburbs with comprehensive home care services.",
    cities: ["Chicago", "Evanston", "Skokie", "Oak Park", "Cicero"],
    image: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Chicago%20skyline%20on%20a%20clear%20sunny%20day%2C%20iconic%20buildings%20and%20Lake%20Michigan%20in%20background%2C%20warm%20golden%20hour%20lighting%2C%20vibrant%20and%20inviting%20cityscape%2C%20high%20quality%20landscape%20photography&width=500&height=320&seq=co1&orientation=landscape",
  },
  {
    name: "Kane County",
    description: "Providing compassionate care across Aurora, Elgin, and all Kane County communities.",
    cities: ["Aurora", "Elgin", "Geneva", "St. Charles", "Batavia"],
    image: "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20a%20charming%20suburban%20Illinois%20town%20with%20tree-lined%20streets%20and%20historic%20buildings%2C%20warm%20autumn%20afternoon%20light%2C%20peaceful%20residential%20neighborhood%2C%20editorial%20photography%20style&width=500&height=320&seq=co2&orientation=landscape",
  },
  {
    name: "Will County",
    description: "Trusted home care services throughout Joliet and the greater Will County area.",
    cities: ["Joliet", "Plainfield", "Bolingbrook", "Romeoville", "Lockport"],
    image: "https://readdy.ai/api/search-image?query=Beautiful%20suburban%20Illinois%20neighborhood%20with%20green%20lawns%20and%20modern%20homes%20under%20a%20bright%20blue%20sky%2C%20peaceful%20family-friendly%20community%2C%20warm%20summer%20day%2C%20editorial%20lifestyle%20photography&width=500&height=320&seq=co3&orientation=landscape",
  },
  {
    name: "DuPage County",
    description: "Premium home care serving Naperville, Wheaton, and all DuPage communities.",
    cities: ["Naperville", "Wheaton", "Downers Grove", "Lombard", "Elmhurst"],
    image: "https://readdy.ai/api/search-image?query=Downtown%20Naperville%20Illinois%20riverwalk%20area%20with%20scenic%20bridge%20and%20trees%2C%20charming%20suburban%20downtown%2C%20warm%20afternoon%20sunlight%2C%20peaceful%20and%20inviting%20atmosphere%2C%20editorial%20photography%20style&width=500&height=320&seq=co4&orientation=landscape",
  },
  {
    name: "Kendall County",
    description: "Expanding quality care to Oswego, Yorkville, and growing Kendall County.",
    cities: ["Oswego", "Yorkville", "Plano", "Montgomery", "Sugar Grove"],
    image: "https://readdy.ai/api/search-image?query=Quiet%20Illinois%20suburban%20street%20with%20well-maintained%20homes%20and%20mature%20trees%2C%20peaceful%20residential%20area%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20tones%2C%20editorial%20lifestyle%20photography&width=500&height=320&seq=co5&orientation=landscape",
  },
  {
    name: "LaSalle County",
    description: "Serving Ottawa, Peru, and LaSalle County with dedicated home care support.",
    cities: ["Ottawa", "Peru", "LaSalle", "Marseilles", "Oglesby"],
    image: "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20a%20small%20historic%20Illinois%20town%20with%20classic%20American%20architecture%2C%20tree-lined%20main%20street%2C%20warm%20afternoon%20light%2C%20charming%20community%20atmosphere%2C%20editorial%20photography%20style&width=500&height=320&seq=co6&orientation=landscape",
  },
];

const quickFacts = [
  { label: "Counties Served", value: "6" },
  { label: "Cities Covered", value: "50+" },
  { label: "Active Clients", value: "500+" },
  { label: "Caregivers on Team", value: "120+" },
];

export default function ServiceAreas() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20map-like%20pattern%20with%20soft%20green%20and%20beige%20tones%2C%20elegant%20geographic%20shapes%2C%20calming%20healthcare%20themed%20background%2C%20minimal%20texture%2C%20warm%20neutral%20colors%2C%20no%20text&width=1600&height=500&seq=areahero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            Coverage Area
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Service Areas
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            Sycamore Oasis proudly serves six counties across Illinois, bringing compassionate home care to communities throughout the greater Chicago area.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 md:py-12 bg-white border-b border-gray-100">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {quickFacts.map((fact) => (
              <div key={fact.label} className="text-center">
                <span className="text-2xl md:text-4xl font-bold text-brand-green-700 block mb-1">
                  {fact.value}
                </span>
                <span className="text-xs md:text-sm text-gray-500">{fact.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Counties */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="areas" ref={setRef("areas")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {/* Map */}
            <div className={`lg:col-span-2 ${animateClass("areas")}`}>
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                <div className="p-4 md:p-6 border-b border-gray-100">
                  <h3 className="text-base font-semibold text-gray-900">Our Service Region</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Headquartered in Naperville, Illinois — serving the greater Chicago area
                  </p>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d190601.123456789!2d-88.2!3d41.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e54c7d0e5e0e1%3A0x1234567890abcdef!2sNaperville%2C%20IL!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sycamore Oasis Service Area Map"
                  className="w-full"
                />
              </div>
            </div>

            {/* County List */}
            <div className={animateClass("areas")}>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Counties We Serve</h3>
              <div className="space-y-3">
                {counties.map((county) => (
                  <div
                    key={county.name}
                    className="bg-white rounded-xl p-4 border border-gray-100 hover:border-brand-green-200 transition-all duration-200"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-fill text-brand-green-600 text-xs" />
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{county.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 ml-6">{county.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* County Cards */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="counties" ref={setRef("counties")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("counties", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Communities
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Cities & Towns We Serve
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              We bring personalized home care to neighborhoods across six Illinois counties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {counties.map((county, i) => (
              <div
                key={county.name}
                className={`group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-brand-green-200 hover:shadow-md transition-all duration-300 ${
                  isVisible["counties"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={county.image}
                    alt={county.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <div className="absolute bottom-3 left-4">
                    <span className="text-white font-semibold text-sm">{county.name}</span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-gray-500 mb-3 leading-relaxed">{county.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {county.cities.map((city) => (
                      <span
                        key={city}
                        className="px-2.5 py-1 text-xs font-medium bg-brand-green-50 text-brand-green-700 rounded-full"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-green-600 hover:text-brand-green-700 transition-colors"
                  >
                    Contact us for {county.name.split(" ")[0]}
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-map-2-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Not Sure If We Cover Your Area?
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Our service area is constantly expanding. Reach out and we will confirm coverage for your specific address.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Check My Address
            </Link>
            <a
              href="tel:+17736997894"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              Call 773-699-7894 (24/7)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}